import React, { useEffect } from 'react';
import { SidePanel, TemplateEditorWrapper } from '@AMIEWEB/Common/TemplateEditor';
import { useReadOnly } from 'oidc/userRoles';
import { usePromiseTracker } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectContacts,
  selectDraftDocument,
  selectPlacementConfirmationTemplatesEditor,
} from 'store/redux-store/placement-client-confirmation-doc-editor/selector';
import {
  pConfirmationDocEditorActions,
  pConfirmationDocEditorReducer,
  pConfirmationDocEditorSliceKey,
} from 'store/redux-store/placement-client-confirmation-doc-editor/slice';
import { ITemplate } from 'store/redux-store/confirmation-template-editor/types';
import { useNavigate, useParams } from 'react-router-dom';
import { useInjectReducer, useInjectSaga } from 'redux-injectors';
import { placementClientConfirmationSaga } from 'store/redux-store/placement-client-confirmation-doc-editor/saga';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { colors } from 'styles/styleVariables';
import { PlacementDetailsStatus } from '../PlacementDetails/PlacementDetailsPage/PlacementDetailsStatus';
import { ContactsWrapper } from './ContactsWrapper';
import { selectEditorPreferences } from 'oidc/UserDevicePreference/userPreference.selectors';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { PreferenceCosmosKeys } from 'oidc/UserDevicePreference/utils';
import { ActionModal, IActionModalProps } from '@AMIEWEB/Common/TemplateEditor/customComponents/ActionModal';

const useStyles = makeStyles()({
  title: {
    fontSize: 16,
    fontWeight: 450,
  },
  clickable: {
    color: colors.amnBlue,
  },
  titleDivider: {
    padding: '0 3px',
    '&::before': {
      content: '"/"',
    },
  },
});

export const DocEditor = () => {
  useInjectReducer({ key: pConfirmationDocEditorSliceKey, reducer: pConfirmationDocEditorReducer });
  useInjectSaga({ key: pConfirmationDocEditorSliceKey, saga: placementClientConfirmationSaga });

  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useNavigate();
  const { placementId } = useParams<{ placementId: string }>();
  const [modalProps, setModalProps] = React.useState<IActionModalProps>({ open: false });
  // @ts-ignore
  const closeModal = React.useCallback(() => setModalProps({ open: false }), []);

  const {
    placementConfirmationDocEditor: { value: editorPreference },
    hasLoadedPreferences,
  } = useSelector(selectEditorPreferences);
  const draftDocument = useSelector(selectDraftDocument);

  const {
    templates,
    documents,
    selectedTemplate,
    mergeTokenList,
    miscellaneous: { forceModalCloseFlag },
    document,
    renderPreviewScreen,
    renderPdfView,
    facilityDefaultTemplate,
    pageAttributes: { sidePanel, forceEditorDirtyState },
  } = useSelector(selectPlacementConfirmationTemplatesEditor);

  const { atleastOneContactIsSelected } = useSelector(selectContacts);

  const { readOnly } = useReadOnly();

  const { promiseInProgress: isLoadingTemplates } = usePromiseTracker({
    area: 'placement-client-confirmation-templates',
    delay: 0,
  });

  const { promiseInProgress: isLoadingDocuments } = usePromiseTracker({
    area: 'placement-client-confirmation-documents',
    delay: 0,
  });

  const { promiseInProgress: previewIsloading } = usePromiseTracker({
    area: 'placement-client-confirmation-templates-preview-document',
    delay: 0,
  });

  const { promiseInProgress: copyIsloading } = usePromiseTracker({
    area: 'placement-client-confirmation-templates-save-copy',
    delay: 0,
  });

  const dispatch = useDispatch();

  const onTemplateClick = React.useCallback(
    card => {
      dispatch(pConfirmationDocEditorActions.business_OnConfirmationDocClick({ card }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [renderPdfView, renderPreviewScreen],
  );

  const onSave = (template: ITemplate) =>
    dispatch(pConfirmationDocEditorActions.saveDraftTemplate({ placementId: parseInt(placementId), template }));

  const onCopy = (documentId: string, hasDraft: boolean | null | undefined) => {
    if(hasDraft){
      setModalProps({
        open: true,
        title: t('templateEditor.modal.title.overrideAmieWebDraft'),
        message: t('templateEditor.modal.message.overrideConfirmationDraft'),
        primaryBtns: [
          {
            text: t('cancelModal.confirmation.yes'),
            onClick: () => {
              closeModal();
              dispatch(pConfirmationDocEditorActions.copyTemplate(
                { placementId: parseInt(placementId), documentId: documentId }
              ));
            },
          },
        ],
        secondaryBtns: [
          {
            text: t('cancelModal.confirmation.cancel'),
            onClick: closeModal,
          },
        ],
      });
    }
    else{
      dispatch(pConfirmationDocEditorActions.copyTemplate({ 
        placementId: parseInt(placementId), documentId: documentId 
      }));
    }
  }

  const updateTemplatePreferences = preference => {
    dispatch(
      userDevicePreferenceActions.business_saveConfirmationEditorPreference({
        editor: 'placementConfirmationDocEditor',
        preference: { id: PreferenceCosmosKeys.placementConfirmationDocEditor, value: preference },
      }),
    );
  };

  const updateEditorWidthPreference = width => {
    dispatch(
      userDevicePreferenceActions.business_saveEditorWidthPreference({
        editor: 'placementConfirmationDocEditor',
        width,
      }),
    );
  };

  useEffect(() => {
    dispatch(pConfirmationDocEditorActions.onInitAction({ placementId: parseInt(placementId) }));

    return () => {
      dispatch(pConfirmationDocEditorActions.reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageTitle = React.useMemo(
    () => (
      <div className={classes.title}>
        <span className={classes.clickable}>{t('templateEditor.placement.allPlacements')}</span>
        <span className={classes.titleDivider} />
        <span className={classes.clickable}>{t('templateEditor.placement.pid', { placementId })}</span>
        <span className={classes.titleDivider} />
        <>{t('templateEditor.placement.confirmationEditor')}</>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placementId],
  );

  const prePageTitleAddOns = React.useMemo(() => [<PlacementDetailsStatus key={'PlacementDetailsStatus'} />], []);

  const changeSidePanel = React.useCallback(
    (panel: SidePanel) => dispatch(pConfirmationDocEditorActions.setSidePanel(panel)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleCreate = (template: ITemplate) => {
    dispatch(pConfirmationDocEditorActions.createConfirmationDoc({ placementId: parseInt(placementId), template }));
  };

  const handlePreview = (templateText: string) =>
    dispatch(
      pConfirmationDocEditorActions.previewConfirmationDoc({
        placementId: parseInt(placementId),
        templateText: templateText,
      }),
    );

  const handleEdit = () => {
    dispatch(pConfirmationDocEditorActions.closePreview());
  };

  const handleCancelEvent = React.useCallback(
    (x, options) => {
      if (options?.resetContacts) dispatch(pConfirmationDocEditorActions.resetContacts());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facilityDefaultTemplate],
  );


  const [{ titleIcons }] = React.useState({ titleIcons: ['documents', 'contacts', 'tokens', 'templates'] });
  const hasDraft = !(draftDocument === null || draftDocument === undefined);

  return(
    <>
      <TemplateEditorWrapper
        //@ts-ignore
        templates={templates}
        //@ts-ignore
        selectedTemplate={selectedTemplate}
        onTemplateClick={onTemplateClick}
        onSave={onSave}
        onCopy={onCopy}
        hasDraft={hasDraft}
        mergeTokenList={mergeTokenList}
        pageTitle={pageTitle}
        prePageTitleAddOns={prePageTitleAddOns}
        isLoading={!hasLoadedPreferences}
        isLoadingSideView={isLoadingTemplates || isLoadingDocuments}
        forceModalCloseFlag={forceModalCloseFlag}
        userIsReadOnly={readOnly}
        restrictToMergeEngine
        history={history}
        placementId={parseInt(placementId)}
        onCreate={handleCreate}
        onPreview={handlePreview}
        contactsSideView={<ContactsWrapper renderPreview={renderPreviewScreen} />}
        document={document}
        renderPreview={renderPreviewScreen}
        renderPdfView={renderPdfView}
        onEdit={handleEdit}
        previewIsloading={previewIsloading || copyIsloading}
        atleastOneContactIsSelected={atleastOneContactIsSelected}
        contactsWarningMessage={t('placement.profile.createConfirmation.warning')}
        onCancelEvent={handleCancelEvent}
        titleIcons={titleIcons}
        documentType="client-confirmation"
        sideViewProps={[sidePanel, changeSidePanel]}
        determineSidePanelExternally
        noDocExists={documents?.length === 0}
        editorPreference={editorPreference}
        updateTemplatePreferences={updateTemplatePreferences}
        onSplitPaneDragEnd={({ clientWidth }) => updateEditorWidthPreference(clientWidth)}
        forceDirtyState={forceEditorDirtyState}
      />
      <ActionModal {...modalProps} />
    </>
    
  );
};
